import { Icon } from '@iconify/react';
import { Button, Input, Spinner } from '@nextui-org/react';
import {
  ApplySettlementReconciliationResult,
  applySettlementReconciliation,
  getSettlementReconciliationState,
  ReconciledSettlementDetailInput,
  ReconciledShipmentAllocationInput,
  SettlementShipmentAllocation,
} from 'common/src/api/settlement-reconciliation';
import { Settlement, SettlementDetails } from 'common/src/utils/types';
import {
  buildSettlementQuadratureProductKey,
  getQuadraturePolicy,
  QuadratureContext,
} from 'common/src/utils/quadrature';
import {
  DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { ShipmentRecord } from '../../store/useShipmentsStore';
import { MapFormatsButton } from '../ui';

const EPSILON = 0.0001;

type ProductFields = {
  variety: string;
  caliber: string;
  color: string;
  format: string;
  label: string;
  unitWeight: number;
  category: string;
};

type ShipmentProductGroup = ProductFields & {
  key: string;
  boxes: ShipmentRecord[];
  availableQuantity: number;
};

type DraftAllocation = {
  sourceDetailId: number;
  shipmentBoxId: string;
  quantity: number;
  receiverSnapshot: Record<string, unknown>;
};

// What is being moved onto a shipped product: a single settlement line (or
// part of it), every unmatched line of one settled product, or a line that is
// already under another shipped product.
type MoveItem =
  | { kind: 'line'; detailId: number; quantity: number }
  | { kind: 'group'; key: string }
  | { kind: 'assigned'; detailId: number; fromKey: string };

type PendingSplit = {
  detailId: number;
  targetKey: string;
  fromKey?: string;
  available: number;
  value: string;
};

type UnmatchedGroup = {
  key: string;
  product: ProductFields;
  lines: { detail: SettlementDetails; remaining: number }[];
  total: number;
};

type ProductEntry = {
  detail: SettlementDetails;
  quantity: number;
  isCorrection: boolean;
};

type Reassignment = {
  detail: SettlementDetails;
  fromKey: string;
  toKey: string;
  quantity: number;
};

interface SettlementQuadratureManagerProps {
  settlement: Settlement;
  details: SettlementDetails[];
  shipmentBoxes: ShipmentRecord[];
  onApplied: (
    result: ApplySettlementReconciliationResult
  ) => void | Promise<void>;
  onPendingChangesChange?: (hasPendingChanges: boolean) => void;
  onMapFormats?: () => void;
  formatsLoading?: boolean;
  // Boxes come from Frutic, not shipment_boxes: nothing to load or persist
  // per box, applying only rewrites the settlement lines.
  externalShipmentBoxes?: boolean;
}

const normalizeText = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase();

const productKey = (product: ProductFields, context: QuadratureContext) =>
  buildSettlementQuadratureProductKey(
    { ...product, unit_weight: product.unitWeight },
    context
  );

const productFromShipment = (box: ShipmentRecord): ProductFields => ({
  variety: box.labeled_variety || box.variety || '',
  caliber: box.caliber || '',
  color: box.color || '',
  format: box.box_format || '',
  label: box.box_label || '',
  unitWeight: Number(box.unit_weight ?? box.box_weight) || 0,
  category: box.category || '',
});

const productFromDetail = (detail: SettlementDetails): ProductFields => ({
  variety: detail.variety || '',
  caliber: detail.caliber || '',
  color: detail.color || '',
  format: detail.format || '',
  label: detail.label || '',
  unitWeight: Number(detail.unit_weight) || 0,
  category: detail.category || '',
});

const receiverSnapshotFromDetail = (detail: SettlementDetails) => ({
  settlement_detail_id: detail.id,
  variety: detail.variety || '',
  caliber: detail.caliber || '',
  color: detail.color || '',
  format: detail.format || '',
  label: detail.label || '',
  unit_weight: Number(detail.unit_weight) || 0,
  category: detail.category || '',
  quantity: Number(detail.quantity) || 0,
});

const compactProductLabel = (
  product: ProductFields,
  includeCategory: boolean
) =>
  [
    product.variety,
    product.caliber,
    product.color,
    product.format,
    product.label,
    product.unitWeight ? `${product.unitWeight} kg` : '',
    includeCategory ? product.category : '',
  ]
    .filter(Boolean)
    .join(' · ');

const productDifferences = (
  from: ProductFields,
  to: ProductFields,
  includeCategory: boolean
) =>
  (
    [
      ['Variedad', from.variety, to.variety],
      ['Calibre', from.caliber, to.caliber],
      ['Color', from.color, to.color],
      ['Formato', from.format, to.format],
      ['Etiqueta', from.label, to.label],
      ['Peso', from.unitWeight, to.unitWeight],
      ...(includeCategory
        ? [['Categoría', from.category, to.category] as const]
        : []),
    ] as const
  ).filter(
    ([, current, target]) => normalizeText(current) !== normalizeText(target)
  );

const formatQuantity = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatPrice = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

// Venta per box of a settlement line. Corrections split a line's venta by
// boxes, so every box keeps this price wherever it ends up.
const linePrice = (detail: SettlementDetails) =>
  Number(detail.quantity) > 0
    ? (Number(detail.local_total_price) || 0) / Number(detail.quantity)
    : 0;

const sumVenta = (entries: { detail: SettlementDetails; quantity: number }[]) =>
  entries.reduce(
    (totals, entry) => ({
      quantity: totals.quantity + entry.quantity,
      total: totals.total + entry.quantity * linePrice(entry.detail),
    }),
    { quantity: 0, total: 0 }
  );

type CorrectionGroup = {
  id: string;
  source: ProductFields;
  price: number;
  quantity: number;
  entries: ProductEntry[];
};

// Corrections that came from the same settled product at the same price read
// as one change, however many lines they span.
const groupCorrections = (
  entries: ProductEntry[],
  keyOf: (detail: SettlementDetails) => string
) => {
  const groups = new Map<string, CorrectionGroup>();
  entries
    .filter((entry) => entry.isCorrection)
    .forEach((entry) => {
      const price = linePrice(entry.detail);
      const id = `${keyOf(entry.detail)}|${price.toFixed(6)}`;
      const group = groups.get(id) || {
        id,
        source: productFromDetail(entry.detail),
        price,
        quantity: 0,
        entries: [],
      };
      group.quantity += entry.quantity;
      group.entries.push(entry);
      groups.set(id, group);
    });
  return Array.from(groups.values());
};

const pricePerBox = (totals: { quantity: number; total: number }) =>
  totals.quantity > EPSILON ? totals.total / totals.quantity : null;

const formatSigned = (value: number) =>
  `${value > 0 ? '+' : ''}${formatQuantity(value)}`;

const allocateTotal = (total: number, weights: number[]) => {
  const denominator = weights.reduce((sum, weight) => sum + weight, 0);
  let assigned = 0;
  return weights.map((weight, index) => {
    const value =
      index === weights.length - 1
        ? total - assigned
        : denominator > 0
          ? (total * weight) / denominator
          : 0;
    assigned += value;
    return value;
  });
};

const mergeAllocation = (
  allocations: DraftAllocation[],
  allocation: DraftAllocation
) => {
  const existing = allocations.find(
    (item) =>
      item.sourceDetailId === allocation.sourceDetailId &&
      item.shipmentBoxId === allocation.shipmentBoxId
  );
  if (existing) {
    existing.quantity += allocation.quantity;
    return;
  }
  allocations.push(allocation);
};

const getSnapshotForDetail = (
  detail: SettlementDetails,
  currentAllocations: SettlementShipmentAllocation[]
) => {
  const persistedSnapshot = currentAllocations.find(
    (allocation) => allocation.settlement_detail_id === detail.id
  )?.receiver_product_snapshot;
  return persistedSnapshot && Object.keys(persistedSnapshot).length > 0
    ? persistedSnapshot
    : receiverSnapshotFromDetail(detail);
};

const DifferenceChips = ({
  differences,
}: {
  differences: ReturnType<typeof productDifferences>;
}) => (
  <div className='flex flex-wrap gap-1'>
    {differences.length === 0 ? (
      <span className='rounded-md bg-success-500/10 px-2 py-0.5 text-[11px] text-success-600 dark:text-success-400'>
        Mismo producto
      </span>
    ) : (
      differences.map(([label, current, target]) => (
        <span
          key={label}
          className='rounded-md bg-default-100 px-2 py-0.5 text-[11px] text-default-500'
        >
          {label}: {String(current || '—')} →{' '}
          <span className='font-medium text-foreground'>
            {String(target || '—')}
          </span>
        </span>
      ))
    )}
  </div>
);

const SettlementQuadratureManager = ({
  settlement,
  details,
  shipmentBoxes,
  onApplied,
  onPendingChangesChange,
  onMapFormats,
  formatsLoading = false,
  externalShipmentBoxes = false,
}: SettlementQuadratureManagerProps) => {
  const [allocations, setAllocations] = useState<DraftAllocation[]>([]);
  const [currentAllocations, setCurrentAllocations] = useState<
    SettlementShipmentAllocation[]
  >([]);
  const [otherUsageByBox, setOtherUsageByBox] = useState<
    Record<string, number>
  >({});
  const [draggedItem, setDraggedItem] = useState<MoveItem | null>(null);
  const [selectedItem, setSelectedItem] = useState<MoveItem | null>(null);
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);
  const [pendingSplit, setPendingSplit] = useState<PendingSplit | null>(null);
  const [moveQuantities, setMoveQuantities] = useState<Record<number, string>>(
    {}
  );
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );
  const [showComplete, setShowComplete] = useState(false);
  const [showImpact, setShowImpact] = useState(false);
  const [expandedCorrections, setExpandedCorrections] = useState<
    Record<string, boolean>
  >({});
  const [expandedExact, setExpandedExact] = useState<Record<string, boolean>>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [autoAssignPending, setAutoAssignPending] = useState(false);
  const [applying, setApplying] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const reconciliationRequestVersion = useRef(0);

  const quadratureContext = useMemo<QuadratureContext>(
    () => ({
      season: settlement.season,
      speciesId: settlement.species_id,
    }),
    [settlement.season, settlement.species_id]
  );
  const quadraturePolicy = useMemo(
    () => getQuadraturePolicy(quadratureContext),
    [quadratureContext]
  );
  const includeCategory = quadraturePolicy.includeCategory;

  const scopedShipmentBoxes = useMemo(() => {
    const reference = normalizeText(settlement.reference_number);
    if (externalShipmentBoxes) {
      // Frutic boxes carry Frutic's own exporter id and are already fetched
      // for the current exporter and species. Their ids are never persisted,
      // so give each one a local id that is guaranteed unique.
      return shipmentBoxes
        .filter((box) => normalizeText(box.reference_number) === reference)
        .map((box, index) => ({ ...box, id: `external-${index}` }));
    }
    return shipmentBoxes.filter(
      (box) =>
        normalizeText(box.reference_number) === reference &&
        Number(box.exporter_id) === Number(settlement.exporter_id) &&
        Number(box.species_id) === Number(settlement.species_id) &&
        (!box.season || box.season === settlement.season)
    );
  }, [externalShipmentBoxes, settlement, shipmentBoxes]);

  // Format mappings regroup products, but do not change the boxing identities
  // underlying draft allocations. Only reload when the available IDs change.
  const scopedShipmentBoxIdsKey = JSON.stringify(
    scopedShipmentBoxes.map((box) => String(box.id)).sort()
  );
  const hasUnmappedFormats = scopedShipmentBoxes.some((box) => {
    const format = normalizeText(box.box_format);
    return (
      Boolean(box.box_type?.trim()) &&
      (!format || ['NA', 'N/A', 'NO ENCONTRADO'].includes(format))
    );
  });

  const shipmentProducts = useMemo(() => {
    const groups = new Map<string, ShipmentProductGroup>();
    scopedShipmentBoxes.forEach((box) => {
      const product = productFromShipment(box);
      const key = productKey(product, quadratureContext);
      const current = groups.get(key) || {
        ...product,
        key,
        boxes: [],
        availableQuantity: 0,
      };
      const otherUsage = Number(otherUsageByBox[String(box.id)]) || 0;
      current.boxes.push(box);
      current.availableQuantity += Math.max(
        0,
        (Number(box.quantity) || 0) - otherUsage
      );
      groups.set(key, current);
    });
    return Array.from(groups.values());
  }, [otherUsageByBox, quadratureContext, scopedShipmentBoxes]);

  const shipmentProductByKey = useMemo(
    () => new Map(shipmentProducts.map((product) => [product.key, product])),
    [shipmentProducts]
  );
  const shipmentProductKeys = JSON.stringify(
    shipmentProducts.map((product) => product.key).sort()
  );

  const shipmentProductKeyByBox = useMemo(() => {
    const map = new Map<string, string>();
    shipmentProducts.forEach((product) =>
      product.boxes.forEach((box) => map.set(String(box.id), product.key))
    );
    return map;
  }, [shipmentProducts]);

  const detailKeyById = useMemo(
    () =>
      new Map(
        details.map((detail) => [
          detail.id,
          productKey(productFromDetail(detail), quadratureContext),
        ])
      ),
    [details, quadratureContext]
  );

  const assignedByDetail = useMemo(() => {
    const map = new Map<number, number>();
    allocations.forEach((allocation) =>
      map.set(
        allocation.sourceDetailId,
        (map.get(allocation.sourceDetailId) || 0) + allocation.quantity
      )
    );
    return map;
  }, [allocations]);

  const assignedByProduct = useMemo(() => {
    const map = new Map<string, number>();
    allocations.forEach((allocation) => {
      const key = shipmentProductKeyByBox.get(allocation.shipmentBoxId);
      if (key) map.set(key, (map.get(key) || 0) + allocation.quantity);
    });
    return map;
  }, [allocations, shipmentProductKeyByBox]);

  const unavailableAllocationCount = useMemo(
    () =>
      allocations.filter(
        (allocation) => !shipmentProductKeyByBox.has(allocation.shipmentBoxId)
      ).length,
    [allocations, shipmentProductKeyByBox]
  );

  // Draft moves of a line's boxes onto a different shipped product.
  const reassignments = useMemo(() => {
    const byDetailAndTarget = new Map<string, Reassignment>();
    allocations.forEach((allocation) => {
      const toKey = shipmentProductKeyByBox.get(allocation.shipmentBoxId);
      const fromKey = detailKeyById.get(allocation.sourceDetailId);
      const detail = details.find(
        (item) => item.id === allocation.sourceDetailId
      );
      if (!toKey || !fromKey || !detail || toKey === fromKey) return;
      const id = `${detail.id}|${toKey}`;
      const current = byDetailAndTarget.get(id) || {
        detail,
        fromKey,
        toKey,
        quantity: 0,
      };
      current.quantity += allocation.quantity;
      byDetailAndTarget.set(id, current);
    });
    return Array.from(byDetailAndTarget.values());
  }, [allocations, detailKeyById, details, shipmentProductKeyByBox]);

  const remainingByProduct = (product: ShipmentProductGroup) =>
    Math.max(
      0,
      product.availableQuantity - (assignedByProduct.get(product.key) || 0)
    );

  // Settlement lines (or the part of them) not yet under a shipped product,
  // grouped by the product the client settled.
  const unmatchedGroups = useMemo(() => {
    const groups = new Map<string, UnmatchedGroup>();
    details.forEach((detail) => {
      const remaining = Math.max(
        0,
        detail.quantity - (assignedByDetail.get(detail.id) || 0)
      );
      if (remaining <= EPSILON) return;
      const key = detailKeyById.get(detail.id)!;
      const group = groups.get(key) || {
        key,
        product: productFromDetail(detail),
        lines: [],
        total: 0,
      };
      group.lines.push({ detail, remaining });
      group.total += remaining;
      groups.set(key, group);
    });
    return Array.from(groups.values()).sort((a, b) => b.total - a.total);
  }, [assignedByDetail, detailKeyById, details]);

  // Settlement lines already under each shipped product.
  const entriesByProduct = useMemo(() => {
    const map = new Map<string, Map<number, ProductEntry>>();
    allocations.forEach((allocation) => {
      const key = shipmentProductKeyByBox.get(allocation.shipmentBoxId);
      const detail = details.find(
        (item) => item.id === allocation.sourceDetailId
      );
      if (!key || !detail) return;
      const entries = map.get(key) || new Map<number, ProductEntry>();
      const entry = entries.get(detail.id) || {
        detail,
        quantity: 0,
        isCorrection: detailKeyById.get(detail.id) !== key,
      };
      entry.quantity += allocation.quantity;
      entries.set(detail.id, entry);
      map.set(key, entries);
    });
    return new Map(
      Array.from(map.entries()).map(([key, entries]) => [
        key,
        Array.from(entries.values()).sort(
          (a, b) =>
            Number(b.isCorrection) - Number(a.isCorrection) ||
            a.detail.id - b.detail.id
        ),
      ])
    );
  }, [allocations, detailKeyById, details, shipmentProductKeyByBox]);

  const availableShipmentTotal = shipmentProducts.reduce(
    (sum, product) => sum + product.availableQuantity,
    0
  );
  const settlementQuantityTotal = details.reduce(
    (sum, detail) => sum + (Number(detail.quantity) || 0),
    0
  );
  const assignedTotal = allocations.reduce(
    (sum, allocation) => sum + allocation.quantity,
    0
  );
  const unassignedSettlementTotal = Math.max(
    0,
    settlementQuantityTotal - assignedTotal
  );
  const unassignedShipmentTotal = Math.max(
    0,
    availableShipmentTotal - assignedTotal
  );
  const totalsMatch =
    Math.abs(availableShipmentTotal - settlementQuantityTotal) < EPSILON;
  const isReady =
    !loadError &&
    unavailableAllocationCount === 0 &&
    shipmentProducts.length > 0 &&
    totalsMatch &&
    unassignedSettlementTotal < EPSILON &&
    unassignedShipmentTotal < EPSILON;
  const reassignedTotal = reassignments.reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  // Assignments (drafted here or saved by other flows, e.g. multi-shipment
  // uploads) that the settlement lines don't reflect yet.
  const hasPendingChanges = reassignments.length > 0;
  const correctedLineCount = new Set(
    reassignments.map((item) => item.detail.id)
  ).size;

  useEffect(() => {
    onPendingChangesChange?.(hasPendingChanges);
  }, [hasPendingChanges, onPendingChangesChange]);

  useEffect(
    () => () => onPendingChangesChange?.(false),
    [onPendingChangesChange]
  );

  const allocateQuantity = useCallback(
    (
      current: DraftAllocation[],
      sourceDetail: SettlementDetails,
      targetProduct: ShipmentProductGroup,
      requestedQuantity: number
    ) => {
      const next = current.map((allocation) => ({ ...allocation }));
      const usedByBox = new Map<string, number>();
      next.forEach((allocation) =>
        usedByBox.set(
          allocation.shipmentBoxId,
          (usedByBox.get(allocation.shipmentBoxId) || 0) + allocation.quantity
        )
      );
      const alreadyAssignedToDetail = next
        .filter((allocation) => allocation.sourceDetailId === sourceDetail.id)
        .reduce((sum, allocation) => sum + allocation.quantity, 0);
      let remaining = Math.min(
        requestedQuantity,
        Math.max(0, sourceDetail.quantity - alreadyAssignedToDetail)
      );
      const snapshot = getSnapshotForDetail(sourceDetail, currentAllocations);

      targetProduct.boxes.forEach((box) => {
        if (remaining <= EPSILON) return;
        const boxId = String(box.id);
        const capacity = Math.max(
          0,
          (Number(box.quantity) || 0) -
            (Number(otherUsageByBox[boxId]) || 0) -
            (usedByBox.get(boxId) || 0)
        );
        if (capacity <= EPSILON) return;
        const quantity = Math.min(remaining, capacity);
        mergeAllocation(next, {
          sourceDetailId: sourceDetail.id,
          shipmentBoxId: boxId,
          quantity,
          receiverSnapshot: snapshot,
        });
        usedByBox.set(boxId, (usedByBox.get(boxId) || 0) + quantity);
        remaining -= quantity;
      });

      return {
        allocations: next,
        assigned: requestedQuantity - remaining,
      };
    },
    [currentAllocations, otherUsageByBox]
  );

  const autoAssignExact = useCallback(
    (baseAllocations: DraftAllocation[]) => {
      let next = baseAllocations.map((allocation) => ({ ...allocation }));
      details.forEach((detail) => {
        const assigned = next
          .filter((allocation) => allocation.sourceDetailId === detail.id)
          .reduce((sum, allocation) => sum + allocation.quantity, 0);
        const remaining = Math.max(0, detail.quantity - assigned);
        if (remaining <= EPSILON) return;
        const exactProduct = shipmentProductByKey.get(
          productKey(productFromDetail(detail), quadratureContext)
        );
        if (!exactProduct) return;
        const result = allocateQuantity(next, detail, exactProduct, remaining);
        if (Math.abs(result.assigned - remaining) < EPSILON) {
          next = result.allocations;
        }
      });
      return next;
    },
    [allocateQuantity, details, quadratureContext, shipmentProductByKey]
  );

  const loadState = useCallback(async () => {
    if (!settlement.id) return;
    const requestVersion = ++reconciliationRequestVersion.current;
    setLoading(true);
    setLoadError('');
    try {
      const state = externalShipmentBoxes
        ? { current_allocations: [], other_usage_by_shipment_box: {} }
        : await getSettlementReconciliationState(
            settlement.id,
            JSON.parse(scopedShipmentBoxIdsKey) as string[]
          );
      if (requestVersion !== reconciliationRequestVersion.current) return;
      setCurrentAllocations(state.current_allocations);
      setOtherUsageByBox(state.other_usage_by_shipment_box);

      const persisted: DraftAllocation[] = state.current_allocations.map(
        (allocation) => ({
          sourceDetailId: allocation.settlement_detail_id,
          shipmentBoxId: String(allocation.shipment_box_id),
          quantity: Number(allocation.allocated_quantity) || 0,
          receiverSnapshot: allocation.receiver_product_snapshot || {},
        })
      );
      setAllocations(persisted);
      setIsDirty(false);
      setAutoAssignPending(true);
    } catch (error) {
      if (requestVersion !== reconciliationRequestVersion.current) return;
      console.error('Error loading settlement reconciliation:', error);
      setAllocations([]);
      setCurrentAllocations([]);
      setOtherUsageByBox({});
      setLoadError('No se pudieron cargar las asignaciones de cuadratura.');
    } finally {
      if (requestVersion === reconciliationRequestVersion.current)
        setLoading(false);
    }
  }, [externalShipmentBoxes, scopedShipmentBoxIdsKey, settlement.id]);

  useEffect(() => {
    void loadState();
    return () => {
      reconciliationRequestVersion.current += 1;
    };
  }, [loadState]);

  // Re-evaluate newly mapped products without discarding existing drafts.
  useEffect(() => {
    if (!formatsLoading) setAutoAssignPending(true);
  }, [formatsLoading, shipmentProductKeys]);

  // Exact matches are assigned silently so only real differences need work.
  useEffect(() => {
    if (loading || formatsLoading || !autoAssignPending) return;
    setAutoAssignPending(false);
    const next = autoAssignExact(allocations);
    const added =
      next.reduce((sum, allocation) => sum + allocation.quantity, 0) -
      assignedTotal;
    if (added > EPSILON) {
      setAllocations(next);
      setIsDirty(true);
    }
  }, [
    allocations,
    assignedTotal,
    autoAssignExact,
    autoAssignPending,
    formatsLoading,
    loading,
  ]);

  const withoutLine = (
    current: DraftAllocation[],
    detailId: number,
    productKeyToRelease: string
  ) =>
    current.filter(
      (allocation) =>
        allocation.sourceDetailId !== detailId ||
        shipmentProductKeyByBox.get(allocation.shipmentBoxId) !==
          productKeyToRelease
    );

  const releaseLine = (detailId: number, key: string) => {
    setAllocations((current) => withoutLine(current, detailId, key));
    setIsDirty(true);
  };

  // Corrects settlement lines onto a shipped product. When the lines come from
  // another shipped product, whatever does not move stays where it was.
  const moveLines = (
    items: { detail: SettlementDetails; quantity: number }[],
    target: ShipmentProductGroup,
    fromKey?: string
  ) => {
    let next = allocations;
    const previousByDetail = new Map<number, number>();
    if (fromKey) {
      items.forEach(({ detail }) => {
        const previous = (entriesByProduct.get(fromKey) || []).find(
          (entry) => entry.detail.id === detail.id
        );
        previousByDetail.set(detail.id, previous?.quantity || 0);
        next = withoutLine(next, detail.id, fromKey);
      });
    }
    let moved = 0;
    items.forEach(({ detail, quantity }) => {
      const result = allocateQuantity(next, detail, target, quantity);
      next = result.allocations;
      moved += result.assigned;
      const source = fromKey ? shipmentProductByKey.get(fromKey) : undefined;
      const rest = (previousByDetail.get(detail.id) || 0) - result.assigned;
      if (source && rest > EPSILON) {
        next = allocateQuantity(next, detail, source, rest).allocations;
      }
    });
    if (moved <= EPSILON) {
      toast.warning('Ese producto despachado ya no tiene bultos sin liquidar');
      return;
    }
    setAllocations(next);
    setIsDirty(true);
    setSelectedItem(null);
    setMoveQuantities((current) => {
      const updated = { ...current };
      items.forEach(({ detail }) => delete updated[detail.id]);
      return updated;
    });
  };

  const dropOnProduct = (item: MoveItem, target: ShipmentProductGroup) => {
    if (settlement.is_blocked) return;
    const gap = Math.floor(remainingByProduct(target) + EPSILON);
    if (gap <= 0) {
      toast.info('Ese producto despachado ya está cuadrado');
      return;
    }
    setPendingSplit(null);

    if (item.kind === 'group') {
      const group = unmatchedGroups.find((entry) => entry.key === item.key);
      if (!group) return;
      let left = gap;
      const items: { detail: SettlementDetails; quantity: number }[] = [];
      group.lines.forEach(({ detail, remaining }) => {
        if (left <= 0) return;
        const quantity = Math.min(remaining, left);
        items.push({ detail, quantity });
        left -= quantity;
        if (quantity < remaining) {
          toast.info(
            `Se dividió la línea ${detail.id}: ${formatQuantity(quantity)} de ${formatQuantity(remaining)} bultos`
          );
        }
      });
      moveLines(items, target);
      return;
    }

    if (item.kind === 'assigned' && item.fromKey === target.key) return;
    const detail = details.find((entry) => entry.id === item.detailId);
    if (!detail) return;
    const quantity =
      item.kind === 'line'
        ? item.quantity
        : (entriesByProduct.get(item.fromKey) || []).find(
            (entry) => entry.detail.id === item.detailId
          )?.quantity || 0;
    const fromKey = item.kind === 'assigned' ? item.fromKey : undefined;
    if (quantity > gap) {
      setPendingSplit({
        detailId: detail.id,
        targetKey: target.key,
        fromKey,
        available: quantity,
        value: String(gap),
      });
      setSelectedItem(null);
      return;
    }
    moveLines([{ detail, quantity }], target, fromKey);
  };

  const buildApplyPayload = () => {
    const outputDetails: ReconciledSettlementDetailInput[] = [];
    const outputAllocations: ReconciledShipmentAllocationInput[] = [];

    details.forEach((detail) => {
      const detailAllocations = allocations.filter(
        (allocation) => allocation.sourceDetailId === detail.id
      );
      const byProduct = new Map<string, DraftAllocation[]>();
      detailAllocations.forEach((allocation) => {
        const key = shipmentProductKeyByBox.get(allocation.shipmentBoxId);
        if (!key) return;
        byProduct.set(key, [...(byProduct.get(key) || []), allocation]);
      });

      const currentKey = productKey(
        productFromDetail(detail),
        quadratureContext
      );
      const groups = Array.from(byProduct.entries()).sort(([a], [b]) => {
        if (a === currentKey) return -1;
        if (b === currentKey) return 1;
        return a.localeCompare(b);
      });
      const quantities = groups.map(([, groupAllocations]) =>
        groupAllocations.reduce(
          (sum, allocation) => sum + allocation.quantity,
          0
        )
      );
      const kilos = groups.map(([key], index) => {
        const product = shipmentProductByKey.get(key)!;
        return quantities[index] * product.unitWeight;
      });
      const sales = allocateTotal(detail.local_total_price || 0, quantities);
      const taxes = allocateTotal(detail.local_total_tax || 0, sales);
      const commissions = allocateTotal(
        detail.local_total_commision || 0,
        sales
      );
      const otherCosts = allocateTotal(detail.local_other_costs || 0, kilos);
      const repacking = allocateTotal(detail.local_repacking_costs || 0, kilos);
      const freight = allocateTotal(detail.usd_freight || 0, kilos);
      const snapshot = getSnapshotForDetail(detail, currentAllocations);

      groups.forEach(([key, groupAllocations], index) => {
        const product = shipmentProductByKey.get(key)!;
        const tempKey = `detail-${detail.id}-${index}`;
        const exchangeRate = Number(detail.exchange_rate) || 1;
        const usdTotalFob =
          (sales[index] -
            taxes[index] -
            commissions[index] -
            otherCosts[index] -
            repacking[index]) /
            exchangeRate -
          freight[index];

        outputDetails.push({
          temp_key: tempKey,
          id: index === 0 ? detail.id : null,
          source_detail_id: detail.id,
          variety: product.variety,
          caliber: product.caliber,
          color: product.color,
          format: product.format,
          label: product.label,
          category: quadraturePolicy.includeCategory
            ? product.category
            : detail.category || '',
          pallet: detail.pallet || '',
          unit_weight: product.unitWeight,
          quantity: quantities[index],
          kilos: kilos[index],
          local_total_price: sales[index],
          local_total_tax: taxes[index],
          local_total_commision: commissions[index],
          local_other_costs: otherCosts[index],
          local_repacking_costs: repacking[index],
          usd_freight: freight[index],
          usd_total_fob: usdTotalFob,
        });

        groupAllocations.forEach((allocation) =>
          outputAllocations.push({
            temp_key: tempKey,
            shipment_box_id: allocation.shipmentBoxId,
            allocated_quantity: allocation.quantity,
            receiver_product_snapshot: snapshot,
          })
        );
      });
    });

    return { details: outputDetails, allocations: outputAllocations };
  };

  const handleApply = async () => {
    if (!isReady || settlement.is_blocked) return;
    setApplying(true);
    try {
      const payload = buildApplyPayload();
      const result = await applySettlementReconciliation({
        settlementId: settlement.id,
        details: payload.details,
        allocations: externalShipmentBoxes ? [] : payload.allocations,
        externalShipmentBoxes,
      });
      await onApplied(result);
      await loadState();
      toast.success('Liquidación cuadrada correctamente');
    } catch (error) {
      console.error('Error applying settlement reconciliation:', error);
      const message =
        error instanceof Error ? error.message : 'No se pudo aplicar';
      toast.error(message);
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className='flex min-h-80 items-center justify-center'>
        <Spinner label='Preparando gestor de cuadratura…' />
      </div>
    );
  }

  if (scopedShipmentBoxes.length === 0) {
    return (
      <div className='rounded-xl border border-dashed border-divider px-6 py-12 text-center'>
        <p className='font-semibold text-foreground'>
          Sin productos enviados para comparar
        </p>
        <p className='mx-auto mt-2 max-w-xl text-sm text-default-500'>
          No encontramos bultos de despacho con la referencia{' '}
          <span className='font-medium'>{settlement.reference_number}</span>.
          Cuando la información de despacho esté disponible podrás asignarla a
          las líneas liquidadas.
        </p>
      </div>
    );
  }

  const label = (product: ProductFields) =>
    compactProductLabel(product, includeCategory);

  const draggedProduct: ProductFields | null = (() => {
    const item = draggedItem || selectedItem;
    if (!item) return null;
    if (item.kind === 'group') {
      return (
        unmatchedGroups.find((group) => group.key === item.key)?.product || null
      );
    }
    const detail = details.find((entry) => entry.id === item.detailId);
    return detail ? productFromDetail(detail) : null;
  })();
  const activeItem = draggedItem || selectedItem;

  const itemId = (item: MoveItem) =>
    item.kind === 'group'
      ? `group|${item.key}`
      : item.kind === 'line'
        ? `line|${item.detailId}`
        : `assigned|${item.detailId}|${item.fromKey}`;

  const startDrag = (event: DragEvent<HTMLElement>, item: MoveItem) => {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', itemId(item));
    setDraggedItem(item);
  };

  const toggleSelected = (item: MoveItem) =>
    setSelectedItem((current) =>
      current && itemId(current) === itemId(item) ? null : item
    );

  const statusMessage = hasPendingChanges ? (
    <>
      <Icon icon='tabler:alert-triangle' className='text-warning-500' />
      <span className='font-medium text-warning-600 dark:text-warning-400'>
        Cambios sin aplicar · se corregirán {formatQuantity(reassignedTotal)}{' '}
        bultos en {correctedLineCount}{' '}
        {correctedLineCount === 1 ? 'línea liquidada' : 'líneas liquidadas'}
      </span>
    </>
  ) : unassignedSettlementTotal > EPSILON ? (
    <>
      <Icon icon='tabler:alert-triangle' className='text-danger-500' />
      <span className='text-default-600'>
        {formatQuantity(unassignedSettlementTotal)} bultos liquidados sin
        cuadrar con el despacho
      </span>
    </>
  ) : unassignedShipmentTotal > EPSILON ? (
    <>
      <Icon icon='tabler:alert-triangle' className='text-danger-500' />
      <span className='text-default-600'>
        Faltan {formatQuantity(unassignedShipmentTotal)} bultos despachados por
        liquidar
      </span>
    </>
  ) : (
    <>
      <Icon icon='tabler:check' className='text-success-500' />
      <span className='font-medium text-success-600 dark:text-success-400'>
        Todo lo liquidado cuadra con el despacho
      </span>
    </>
  );

  const currency = details[0]?.local_currency || '';

  // Boxes (and their venta) that would land on a product if the active item
  // were dropped there now, following the same rules as dropOnProduct.
  const previewMove = (item: MoveItem, target: ShipmentProductGroup) => {
    const gap = Math.floor(remainingByProduct(target) + EPSILON);
    if (gap <= 0) return null;
    if (item.kind === 'group') {
      const group = unmatchedGroups.find((entry) => entry.key === item.key);
      if (!group) return null;
      let left = gap;
      const moved: { detail: SettlementDetails; quantity: number }[] = [];
      group.lines.forEach(({ detail, remaining }) => {
        if (left <= 0) return;
        const quantity = Math.min(remaining, left);
        moved.push({ detail, quantity });
        left -= quantity;
      });
      return sumVenta(moved);
    }
    if (item.kind === 'assigned' && item.fromKey === target.key) return null;
    const detail = details.find((entry) => entry.id === item.detailId);
    if (!detail) return null;
    const quantity =
      item.kind === 'line'
        ? item.quantity
        : (entriesByProduct.get(item.fromKey) || []).find(
            (entry) => entry.detail.id === item.detailId
          )?.quantity || 0;
    return sumVenta([{ detail, quantity: Math.min(quantity, gap) }]);
  };

  const ventaLabel = (price: number | null) =>
    price === null ? '—' : `${formatPrice(price)} ${currency}/caja`;

  const correctionKeyOf = (detail: SettlementDetails) =>
    detailKeyById.get(detail.id) || '';

  const hasCorrection = (product: ShipmentProductGroup) =>
    (entriesByProduct.get(product.key) || []).some(
      (entry) => entry.isCorrection
    );

  const renderShippedProduct = (product: ShipmentProductGroup) => {
    const settled = assignedByProduct.get(product.key) || 0;
    const missing = remainingByProduct(product);
    const entries = entriesByProduct.get(product.key) || [];
    const accepts = Boolean(activeItem) && missing > EPSILON;
    const isOver = dropTargetKey === product.key;
    const differences = draggedProduct
      ? productDifferences(draggedProduct, product, includeCategory)
      : [];
    const split = pendingSplit?.targetKey === product.key ? pendingSplit : null;
    // While a line is active, products with room are green when moving it
    // there changes at most one field, yellow when it changes more.
    const likelyMatch = differences.length <= 1;
    const corrections = entries.filter((entry) => entry.isCorrection);
    const exactEntries = entries.filter((entry) => !entry.isCorrection);
    const showExact = expandedExact[product.key] ?? corrections.length === 0;
    const venta = sumVenta(entries);
    const exactVenta = sumVenta(exactEntries);
    const correctedVenta = sumVenta(corrections);
    const preview =
      accepts && activeItem ? previewMove(activeItem, product) : null;

    return (
      <div
        key={product.key}
        className={`rounded-xl border-2 bg-content1 transition-colors ${
          !accepts
            ? `border-divider ${activeItem ? 'opacity-50' : ''}`
            : likelyMatch
              ? `border-success-500 ${isOver ? 'bg-success-50/60 dark:bg-success-100/10' : 'bg-success-50/20 dark:bg-success-100/5'}`
              : `border-warning-500 ${isOver ? 'bg-warning-50/60 dark:bg-warning-100/10' : 'bg-warning-50/20 dark:bg-warning-100/5'}`
        } ${selectedItem && accepts ? 'cursor-pointer' : ''}`}
        onDragOver={(event) => {
          if (!accepts) return;
          event.preventDefault();
          setDropTargetKey(product.key);
        }}
        onDragLeave={() =>
          setDropTargetKey((current) =>
            current === product.key ? null : current
          )
        }
        onDrop={(event) => {
          event.preventDefault();
          setDropTargetKey(null);
          if (draggedItem) dropOnProduct(draggedItem, product);
          setDraggedItem(null);
        }}
        onClick={() => {
          if (selectedItem && accepts) dropOnProduct(selectedItem, product);
        }}
      >
        <div className='flex flex-wrap items-start justify-between gap-3 px-4 py-3'>
          <div className='min-w-0 space-y-1'>
            <p className='text-sm font-medium text-foreground'>
              {label(product)}
            </p>
            <p className='text-xs tabular-nums text-default-500'>
              Despachado {formatQuantity(product.availableQuantity)} · Liquidado{' '}
              {formatQuantity(settled)} ·{' '}
              <span className='font-medium text-foreground'>
                Venta {ventaLabel(pricePerBox(venta))}
              </span>
            </p>
            {corrections.length > 0 && (
              <p className='text-[11px] tabular-nums text-default-500'>
                {exactEntries.length > 0 && (
                  <>
                    Exactas {formatQuantity(exactVenta.quantity)} @
                    {formatPrice(pricePerBox(exactVenta) || 0)} ·{' '}
                  </>
                )}
                Corregidas {formatQuantity(correctedVenta.quantity)} @
                {formatPrice(pricePerBox(correctedVenta) || 0)}
              </p>
            )}
            {accepts && draggedProduct && (
              <>
                <p
                  className={`text-[11px] font-semibold ${
                    likelyMatch
                      ? 'text-success-600 dark:text-success-400'
                      : 'text-warning-600 dark:text-warning-400'
                  }`}
                >
                  {differences.length === 0
                    ? 'Mismo producto'
                    : differences.length === 1
                      ? '1 cambio'
                      : `${differences.length} cambios`}
                </p>
                <DifferenceChips differences={differences} />
                {preview && (
                  <p className='text-[11px] font-medium tabular-nums text-primary'>
                    Al soltar: +{formatQuantity(preview.quantity)} bultos @
                    {formatPrice(pricePerBox(preview) || 0)} · Venta{' '}
                    {ventaLabel(pricePerBox(venta))} →{' '}
                    {ventaLabel(
                      pricePerBox({
                        quantity: venta.quantity + preview.quantity,
                        total: venta.total + preview.total,
                      })
                    )}
                  </p>
                )}
              </>
            )}
          </div>
          {missing > EPSILON ? (
            <span className='whitespace-nowrap rounded-full bg-danger-500/10 px-2 py-0.5 text-[11px] font-semibold text-danger-600 dark:text-danger-400'>
              Faltan {formatQuantity(missing)}
            </span>
          ) : hasCorrection(product) ? (
            <span className='whitespace-nowrap rounded-full bg-warning-500/10 px-2 py-0.5 text-[11px] font-semibold text-warning-600 dark:text-warning-400'>
              Cuadrado · falta aplicar
            </span>
          ) : (
            <span className='inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-success-500/10 px-2 py-0.5 text-[11px] font-semibold text-success-600 dark:text-success-400'>
              <Icon icon='tabler:check' className='h-2.5 w-2.5' /> Cuadrado
            </span>
          )}
        </div>

        {split && (
          <div
            className='flex flex-wrap items-center gap-2 border-t border-divider bg-warning-50/40 px-4 py-2 text-xs dark:bg-warning-100/5'
            onClick={(event) => event.stopPropagation()}
          >
            <span className='text-default-600'>
              La línea {split.detailId} tiene {formatQuantity(split.available)}{' '}
              bultos y faltan {formatQuantity(missing)}. Dividir y corregir:
            </span>
            <Input
              size='sm'
              type='number'
              min={1}
              max={Math.min(split.available, missing)}
              step={1}
              value={split.value}
              onValueChange={(value) =>
                setPendingSplit((current) =>
                  current ? { ...current, value } : current
                )
              }
              aria-label='Bultos a corregir'
              className='w-24'
              classNames={{ input: 'text-right tabular-nums' }}
              autoFocus
            />
            <Button
              size='sm'
              color='primary'
              isDisabled={
                !Number.isInteger(Number(split.value)) ||
                Number(split.value) <= 0 ||
                Number(split.value) > Math.min(split.available, missing)
              }
              onPress={() => {
                const detail = details.find(
                  (entry) => entry.id === split.detailId
                );
                if (detail) {
                  moveLines(
                    [{ detail, quantity: Number(split.value) }],
                    product,
                    split.fromKey
                  );
                }
                setPendingSplit(null);
              }}
            >
              Corregir {split.value || 0} bultos
            </Button>
            <Button
              size='sm'
              variant='light'
              onPress={() => setPendingSplit(null)}
            >
              Cancelar
            </Button>
          </div>
        )}

        {entries.length > 0 && (
          <div
            className='space-y-1 border-t border-divider px-2 py-2'
            onClick={(event) => event.stopPropagation()}
          >
            {exactEntries.length > 0 && (
              <button
                type='button'
                className='flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-default-500 hover:bg-default-100/60'
                onClick={() =>
                  setExpandedExact((current) => ({
                    ...current,
                    [product.key]: !showExact,
                  }))
                }
              >
                {showExact ? (
                  <Icon icon='tabler:chevron-down' className='h-2.5 w-2.5' />
                ) : (
                  <Icon icon='tabler:chevron-right' className='h-2.5 w-2.5' />
                )}
                <Icon
                  icon='tabler:check'
                  className='h-2.5 w-2.5 text-success-500'
                />
                {exactEntries.length}{' '}
                {exactEntries.length === 1 ? 'línea exacta' : 'líneas exactas'}{' '}
                ·{' '}
                <span className='tabular-nums'>
                  {formatQuantity(
                    exactEntries.reduce((sum, entry) => sum + entry.quantity, 0)
                  )}{' '}
                  bultos
                </span>
              </button>
            )}
            {groupCorrections(corrections, correctionKeyOf).map((group) => {
              const groupId = `${product.key}|${group.id}`;
              const expanded = expandedCorrections[groupId] ?? false;
              return (
                <div
                  key={group.id}
                  className='rounded-lg border border-warning-300/40 bg-warning-50/30 dark:bg-warning-100/5'
                >
                  <div className='flex flex-wrap items-center justify-between gap-2 px-2 py-1.5'>
                    <div className='min-w-0 space-y-1'>
                      <p className='text-xs text-foreground'>
                        <span className='tabular-nums'>
                          {formatQuantity(group.quantity)} bultos @
                          {formatPrice(group.price)} {currency}
                        </span>{' '}
                        <span className='text-default-500'>
                          · {group.entries.length}{' '}
                          {group.entries.length === 1 ? 'línea' : 'líneas'} de{' '}
                          {label(group.source)}
                        </span>
                      </p>
                      <DifferenceChips
                        differences={productDifferences(
                          group.source,
                          product,
                          includeCategory
                        )}
                      />
                    </div>
                    <div className='flex items-center gap-1'>
                      <Button
                        size='sm'
                        variant='light'
                        isIconOnly
                        aria-label={expanded ? 'Ocultar líneas' : 'Ver líneas'}
                        onPress={() =>
                          setExpandedCorrections((current) => ({
                            ...current,
                            [groupId]: !expanded,
                          }))
                        }
                      >
                        {expanded ? (
                          <Icon
                            icon='tabler:chevron-down'
                            className='h-3 w-3 text-default-400'
                          />
                        ) : (
                          <Icon
                            icon='tabler:chevron-right'
                            className='h-3 w-3 text-default-400'
                          />
                        )}
                      </Button>
                      <Button
                        size='sm'
                        variant='light'
                        isIconOnly
                        aria-label='Quitar estas líneas de este producto'
                        onPress={() =>
                          group.entries.forEach((entry) =>
                            releaseLine(entry.detail.id, product.key)
                          )
                        }
                        isDisabled={settlement.is_blocked}
                      >
                        <Icon
                          icon='tabler:x'
                          className='h-3 w-3 text-default-400'
                        />
                      </Button>
                    </div>
                  </div>
                  {expanded &&
                    group.entries.map((entry) =>
                      renderPlacedLine(entry, product)
                    )}
                </div>
              );
            })}
            {showExact &&
              exactEntries.map((entry) => renderPlacedLine(entry, product))}
          </div>
        )}
      </div>
    );
  };

  const renderPlacedLine = (
    entry: ProductEntry,
    product: ShipmentProductGroup
  ) => {
    const item: MoveItem = {
      kind: 'assigned',
      detailId: entry.detail.id,
      fromKey: product.key,
    };
    return (
      <div
        key={entry.detail.id}
        draggable={!settlement.is_blocked}
        onDragStart={(event) => startDrag(event, item)}
        onDragEnd={() => setDraggedItem(null)}
        className='flex flex-wrap items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-default-100/60'
      >
        <div className='flex min-w-0 items-center gap-2'>
          <Icon
            icon='tabler:grip-vertical'
            className='h-3 w-3 shrink-0 cursor-grab text-default-300'
          />
          <div className='min-w-0 space-y-1'>
            <p className='text-xs text-foreground'>
              Línea {entry.detail.id} ·{' '}
              <span className='tabular-nums'>
                {formatQuantity(entry.quantity)} bultos @
                {formatPrice(linePrice(entry.detail))} {currency}
              </span>
              {!entry.isCorrection && (
                <span className='text-default-400'> · exacta</span>
              )}
            </p>
          </div>
        </div>
        <Button
          size='sm'
          variant='light'
          isIconOnly
          aria-label='Quitar línea de este producto'
          onPress={() => releaseLine(entry.detail.id, product.key)}
          isDisabled={settlement.is_blocked}
        >
          <Icon icon='tabler:x' className='h-3 w-3 text-default-400' />
        </Button>
      </div>
    );
  };

  const renderUnmatchedLine = (
    detail: SettlementDetails,
    remaining: number
  ) => {
    const moveValue = moveQuantities[detail.id];
    const moveQuantity = Math.min(
      remaining,
      Math.floor(Number(moveValue) || remaining)
    );
    const item: MoveItem = {
      kind: 'line',
      detailId: detail.id,
      quantity: moveQuantity,
    };
    const selected =
      selectedItem?.kind === 'line' && selectedItem.detailId === detail.id;
    return (
      <div
        key={detail.id}
        draggable={!settlement.is_blocked}
        onDragStart={(event) => startDrag(event, item)}
        onDragEnd={() => setDraggedItem(null)}
        onClick={(event) => {
          event.stopPropagation();
          toggleSelected(item);
        }}
        className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-2 py-1.5 ${
          selected
            ? 'border-primary bg-primary-50/40 dark:bg-primary-100/10'
            : 'border-transparent hover:bg-default-100/60'
        }`}
      >
        <div className='flex min-w-0 items-center gap-2'>
          <Icon
            icon='tabler:grip-vertical'
            className='h-3 w-3 shrink-0 cursor-grab text-default-300'
          />
          <p className='text-xs text-foreground'>
            Línea {detail.id} ·{' '}
            <span className='tabular-nums'>
              {moveValue !== undefined
                ? `${formatQuantity(moveQuantity)} de ${formatQuantity(remaining)}`
                : formatQuantity(remaining)}{' '}
              bultos
            </span>
            <span className='text-default-400'>
              {' '}
              · @{formatPrice(linePrice(detail))} {detail.local_currency}/caja
            </span>
          </p>
        </div>
        <div
          className='flex items-center gap-1'
          onClick={(event) => event.stopPropagation()}
        >
          {moveValue !== undefined && (
            <Input
              size='sm'
              type='number'
              min={1}
              max={remaining}
              step={1}
              value={moveValue}
              onValueChange={(value) =>
                setMoveQuantities((current) => ({
                  ...current,
                  [detail.id]: value,
                }))
              }
              aria-label='Bultos a mover'
              className='w-20'
              classNames={{ input: 'text-right tabular-nums' }}
            />
          )}
          <Button
            size='sm'
            variant='light'
            isIconOnly
            aria-label={
              moveValue !== undefined
                ? 'Mover la línea completa'
                : 'Dividir línea'
            }
            onPress={() =>
              setMoveQuantities((current) => {
                const next = { ...current };
                if (moveValue !== undefined) delete next[detail.id];
                else next[detail.id] = String(remaining);
                return next;
              })
            }
            isDisabled={settlement.is_blocked || remaining <= 1}
          >
            {moveValue !== undefined ? (
              <Icon icon='tabler:x' className='h-3 w-3 text-default-400' />
            ) : (
              <Icon icon='tabler:cut' className='h-3 w-3 text-default-400' />
            )}
          </Button>
        </div>
      </div>
    );
  };

  const impactProducts = shipmentProducts
    .filter(hasCorrection)
    .sort((a, b) => label(a).localeCompare(label(b), 'es'));
  // A shipped product's venta per box only changes when boxes settled at
  // different prices end up under it; a pure relabel keeps the price.
  const priceRange = (product: ShipmentProductGroup) => {
    const prices = (entriesByProduct.get(product.key) || []).map((entry) =>
      linePrice(entry.detail)
    );
    const min = prices.length ? Math.min(...prices) : 0;
    const max = prices.length ? Math.max(...prices) : 0;
    return { min, max, mixed: max - min > 0.005 };
  };
  const mixedProductCount = impactProducts.filter(
    (product) => priceRange(product).mixed
  ).length;
  const totalVenta = details.reduce(
    (sum, detail) => sum + (Number(detail.local_total_price) || 0),
    0
  );

  // Products still missing boxes, or with corrections waiting to be applied,
  // stay visible; only products that already match without changes collapse.
  const needsReview = (product: ShipmentProductGroup) =>
    remainingByProduct(product) > EPSILON || hasCorrection(product);
  const openProducts = shipmentProducts
    .filter(needsReview)
    .sort(
      (a, b) =>
        remainingByProduct(b) - remainingByProduct(a) ||
        Number(hasCorrection(b)) - Number(hasCorrection(a)) ||
        label(a).localeCompare(label(b), 'es')
    );
  const completeProducts = shipmentProducts
    .filter((product) => !needsReview(product))
    .sort((a, b) => label(a).localeCompare(label(b), 'es'));

  return (
    <div className='space-y-4' onClick={() => setSelectedItem(null)}>
      <div className='sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 border-b border-divider bg-content1/95 py-3 backdrop-blur'>
        <div className='flex items-center gap-2 text-sm'>
          {statusMessage}
          <span className='text-xs tabular-nums text-default-400'>
            · Liquidado {formatQuantity(settlementQuantityTotal)} · Despachado{' '}
            {formatQuantity(availableShipmentTotal)}
          </span>
        </div>
        <div className='flex items-center gap-2'>
          {hasUnmappedFormats && onMapFormats && (
            <MapFormatsButton onPress={onMapFormats} isDisabled={applying} />
          )}
          <Button
            size='sm'
            variant='light'
            startContent={<Icon icon='tabler:arrow-back-up' />}
            onPress={() => void loadState()}
            isDisabled={!isDirty || applying}
          >
            Restablecer
          </Button>
          <Button
            size='sm'
            color='primary'
            onPress={handleApply}
            isLoading={applying}
            isDisabled={
              !isReady ||
              (!isDirty && !hasPendingChanges) ||
              settlement.is_blocked
            }
          >
            Aplicar cuadratura
          </Button>
        </div>
      </div>

      {hasPendingChanges && (
        <div
          className='rounded-xl border border-warning-300/40 bg-content1'
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type='button'
            className='flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left text-sm font-medium text-foreground'
            onClick={() => setShowImpact((current) => !current)}
          >
            {showImpact ? (
              <Icon icon='tabler:chevron-down' className='h-3 w-3' />
            ) : (
              <Icon icon='tabler:chevron-right' className='h-3 w-3' />
            )}
            Resumen de correcciones
            <span className='text-xs font-normal text-default-500'>
              · {correctedLineCount}{' '}
              {correctedLineCount === 1
                ? 'línea corregida'
                : 'líneas corregidas'}{' '}
              ·{' '}
              {mixedProductCount === 0
                ? 'ningún precio de venta cambia'
                : `${mixedProductCount} ${
                    mixedProductCount === 1
                      ? 'producto mezcla precios'
                      : 'productos mezclan precios'
                  }`}{' '}
              · venta total {formatPrice(totalVenta)} {currency} sin cambios
            </span>
          </button>
          {showImpact && (
            <div className='overflow-x-auto border-t border-divider'>
              <table className='w-full min-w-[640px] text-xs'>
                <thead>
                  <tr className='text-left text-default-500'>
                    <th className='px-4 py-2 font-medium'>
                      Producto despachado
                    </th>
                    <th className='px-2 py-2 font-medium'>
                      Qué cambia en las líneas liquidadas
                    </th>
                    <th className='px-4 py-2 text-right font-medium'>
                      Precio de venta
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {impactProducts.map((product) => {
                    const entries = entriesByProduct.get(product.key) || [];
                    const corrected = groupCorrections(
                      entries,
                      correctionKeyOf
                    );
                    const prices = priceRange(product);
                    return (
                      <tr
                        key={product.key}
                        className='border-t border-divider align-top'
                      >
                        <td className='px-4 py-2 text-foreground'>
                          {label(product)}
                        </td>
                        <td className='space-y-2 px-2 py-2'>
                          {corrected.map((group) => (
                            <div key={group.id} className='space-y-1'>
                              <p className='tabular-nums text-default-500'>
                                {formatQuantity(group.quantity)} bultos ·{' '}
                                {group.entries.length}{' '}
                                {group.entries.length === 1
                                  ? 'línea'
                                  : 'líneas'}{' '}
                                @{formatPrice(group.price)}
                              </p>
                              <DifferenceChips
                                differences={productDifferences(
                                  group.source,
                                  product,
                                  includeCategory
                                )}
                              />
                            </div>
                          ))}
                        </td>
                        <td className='px-4 py-2 text-right tabular-nums'>
                          {prices.mixed ? (
                            <>
                              <p className='font-medium text-warning-600 dark:text-warning-400'>
                                Mezcla {formatPrice(prices.min)}–
                                {formatPrice(prices.max)} →{' '}
                                {ventaLabel(pricePerBox(sumVenta(entries)))}
                              </p>
                              <p className='text-default-400'>
                                promedio ponderado por bultos
                              </p>
                            </>
                          ) : (
                            <>
                              <p className='font-medium text-foreground'>
                                {ventaLabel(prices.min)}
                              </p>
                              <p className='text-default-400'>sin cambio</p>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loadError && (
        <div className='rounded-lg border border-warning-300/50 bg-warning-50/60 px-3 py-2 text-sm text-warning-700 dark:bg-warning-100/10 dark:text-warning-400'>
          {loadError}
        </div>
      )}

      {unavailableAllocationCount > 0 && (
        <div className='rounded-lg border border-warning-300/50 bg-warning-50/60 px-3 py-2 text-sm text-warning-700 dark:bg-warning-100/10 dark:text-warning-400'>
          Hay {unavailableAllocationCount}{' '}
          {unavailableAllocationCount === 1 ? 'asignación' : 'asignaciones'} a
          bultos que no están disponibles en la vista actual. Restablece o
          recarga los datos de despacho antes de aplicar.
        </div>
      )}

      {!totalsMatch && (
        <div className='flex items-start gap-2 rounded-lg border border-warning-300/50 bg-warning-50/60 px-3 py-2 text-sm text-warning-700 dark:bg-warning-100/10 dark:text-warning-400'>
          <Icon icon='tabler:alert-triangle' className='mt-0.5 shrink-0' />
          <span>
            Los totales no coinciden: hay una diferencia de{' '}
            <strong>
              {formatSigned(settlementQuantityTotal - availableShipmentTotal)}
            </strong>{' '}
            bultos entre lo liquidado y lo disponible del despacho. Corregir
            productos de las líneas no alcanza para cuadrarla.
          </span>
        </div>
      )}

      <div className='grid items-start gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]'>
        <section className='space-y-2'>
          <div className='px-1'>
            <p className='text-sm font-semibold text-foreground'>
              Productos despachados
            </p>
            <p className='text-xs text-default-500'>
              Referencia fija. Debajo de cada uno, las líneas liquidadas que le
              corresponden.
            </p>
          </div>
          {openProducts.map(renderShippedProduct)}
          {completeProducts.length > 0 && (
            <button
              type='button'
              className='flex w-full items-center gap-2 rounded-xl border border-divider px-4 py-3 text-left text-sm text-default-500 hover:bg-default-100/60'
              onClick={(event) => {
                event.stopPropagation();
                setShowComplete((current) => !current);
              }}
            >
              {showComplete ? (
                <Icon icon='tabler:chevron-down' className='h-3 w-3' />
              ) : (
                <Icon icon='tabler:chevron-right' className='h-3 w-3' />
              )}
              <Icon icon='tabler:check' className='text-success-500' />
              {completeProducts.length}{' '}
              {completeProducts.length === 1
                ? 'producto cuadrado'
                : 'productos cuadrados'}
            </button>
          )}
          {showComplete && completeProducts.map(renderShippedProduct)}
        </section>

        <section
          className={`space-y-2 rounded-xl border p-3 lg:sticky lg:top-16 lg:max-h-[70vh] lg:overflow-y-auto ${
            draggedItem?.kind === 'assigned'
              ? 'border-primary/40 bg-primary-50/20 dark:bg-primary-100/5'
              : 'border-divider bg-content1'
          }`}
          onDragOver={(event) => {
            if (draggedItem?.kind === 'assigned') event.preventDefault();
          }}
          onDrop={(event) => {
            event.preventDefault();
            if (draggedItem?.kind === 'assigned') {
              releaseLine(draggedItem.detailId, draggedItem.fromKey);
            }
            setDraggedItem(null);
          }}
        >
          <div>
            <p className='text-sm font-semibold text-foreground'>
              Líneas liquidadas sin cuadrar
            </p>
            <p className='text-xs text-default-500'>
              Arrastra una línea o un grupo a un producto despachado (o
              selecciónalo y haz clic en el producto). Usa ✂ para dividir.
            </p>
          </div>
          {unmatchedGroups.length === 0 ? (
            <p className='rounded-lg border border-dashed border-divider px-3 py-6 text-center text-sm text-default-500'>
              No quedan líneas sin cuadrar.
            </p>
          ) : (
            unmatchedGroups.map((group) => {
              const item: MoveItem = { kind: 'group', key: group.key };
              const selected =
                selectedItem?.kind === 'group' &&
                selectedItem.key === group.key;
              const expanded =
                expandedGroups[group.key] ?? group.lines.length <= 3;
              return (
                <div
                  key={group.key}
                  className={`rounded-lg border ${
                    selected ? 'border-primary' : 'border-divider'
                  }`}
                >
                  <div
                    draggable={!settlement.is_blocked}
                    onDragStart={(event) => startDrag(event, item)}
                    onDragEnd={() => setDraggedItem(null)}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleSelected(item);
                    }}
                    className={`flex items-start justify-between gap-2 rounded-t-lg px-3 py-2 ${
                      selected
                        ? 'bg-primary-50/40 dark:bg-primary-100/10'
                        : 'hover:bg-default-100/60'
                    }`}
                  >
                    <div className='flex min-w-0 items-start gap-2'>
                      <Icon
                        icon='tabler:grip-vertical'
                        className='mt-1 h-3 w-3 shrink-0 cursor-grab text-default-300'
                      />
                      <div className='min-w-0'>
                        <p className='text-sm font-medium text-foreground'>
                          {label(group.product)}
                        </p>
                        <p className='text-xs tabular-nums text-default-500'>
                          {formatQuantity(group.total)} bultos ·{' '}
                          {group.lines.length}{' '}
                          {group.lines.length === 1 ? 'línea' : 'líneas'} ·{' '}
                          {(() => {
                            const prices = group.lines.map(({ detail }) =>
                              linePrice(detail)
                            );
                            const min = Math.min(...prices);
                            const max = Math.max(...prices);
                            return max - min > EPSILON
                              ? `@${formatPrice(min)}–${formatPrice(max)}`
                              : `@${formatPrice(min)}`;
                          })()}{' '}
                          {currency}/caja
                        </p>
                      </div>
                    </div>
                    <button
                      type='button'
                      className='shrink-0 p-1 text-default-400'
                      aria-label={expanded ? 'Ocultar líneas' : 'Ver líneas'}
                      onClick={(event) => {
                        event.stopPropagation();
                        setExpandedGroups((current) => ({
                          ...current,
                          [group.key]: !expanded,
                        }));
                      }}
                    >
                      {expanded ? (
                        <Icon icon='tabler:chevron-down' className='h-3 w-3' />
                      ) : (
                        <Icon icon='tabler:chevron-right' className='h-3 w-3' />
                      )}
                    </button>
                  </div>
                  {expanded && (
                    <div className='space-y-1 border-t border-divider p-1'>
                      {group.lines.map(({ detail, remaining }) =>
                        renderUnmatchedLine(detail, remaining)
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </section>
      </div>
    </div>
  );
};

export default SettlementQuadratureManager;
